import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api";
import { CountryInput } from "../components/CountryInput";
import { GiveUp } from "../components/GiveUp";
import { RadarLog } from "../components/radar/RadarLog";
import { type RadarGuess, RadarScope } from "../components/radar/RadarScope";
import { ResultSheet } from "../components/ResultSheet";
import { Centered, Header, Shell } from "../components/Shell";
import { Toast } from "../components/Toast";
import { useGame } from "../useGame";

interface View {
  guesses: RadarGuess[];
  answer?: { code: string; name: string };
}

const COPY = { unknown_country: "Не знаю такої країни", already_guessed: "Вже було" };
/** The lock rings / static burst get their moment before the sheet slides up. */
const OPTS = { sheetDelayMs: 1900 };

/**
 * Worldle «Радар»: today's silhouette is the target at the centre of a radar
 * scope; every guess blips in where it lies relative to it. The log below keeps
 * the exact numbers.
 */
export function Worldle({ date }: { date?: string | undefined }) {
  const g = useGame<View>("worldle", COPY, date, OPTS);
  const [shape, setShape] = useState<string | null>(null);
  const restored = useRef<number | null>(null);

  useEffect(() => {
    let url: string | null = null;
    void api.shape(date).then((u) => {
      url = u;
      setShape(u);
    });
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  const state = g.screen.kind === "game" ? g.screen.state : null;
  const finished = state !== null && state.status !== "playing";
  const guessed = useMemo(() => new Set(state?.view.guesses.map((x) => x.name) ?? []), [state?.view.guesses]);

  if (g.screen.kind === "loading") return <Shell><Centered>…</Centered></Shell>;
  if (g.screen.kind === "error") {
    return (
      <Shell>
        <Centered>
          <div>{g.screen.message}</div>
          {g.screen.retry && (
            <button type="button" onClick={() => void g.load()} className="rounded-xl bg-tgbutton px-5 py-2.5 text-sm font-semibold text-tgbuttontext">
              ще раз
            </button>
          )}
        </Centered>
      </Shell>
    );
  }
  const s = g.screen.state;
  if (restored.current === null) restored.current = s.view.guesses.length;

  return (
    <Shell header={<Header left={date ? `📼 ${date}` : `#${s.number}`} title="WORLDLE" right={date ? "" : `🔥 ${s.me.streak}`} />}>
      <CountryInput
        disabled={finished}
        busy={g.busy}
        exclude={guessed}
        onSubmit={(name) => void g.run(() => api.guess<View>("worldle", name, date))}
      />

      <div
        className="mx-auto w-full px-5 py-2"
        // Height-bound on short screens so the log keeps a few rows in view.
        style={{ maxWidth: "min(360px, calc(var(--tg-viewport-stable-height, 100vh) - 300px))" }}
      >
        <RadarScope shape={shape} guesses={s.view.guesses} status={s.status} answer={s.view.answer?.name} />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-2">
        {s.view.guesses.length === 0 ? (
          <div className="rd pt-2 text-center text-[12px] opacity-60">ціль на радарі — назви країну</div>
        ) : (
          <RadarLog guesses={s.view.guesses} restored={restored.current} />
        )}
      </div>

      {!finished && (
        <div className="flex gap-2 px-4 pb-[calc(var(--tg-safe-area-inset-bottom,0px)+10px)] pt-1">
          <div className="flex flex-1 items-center text-xs text-tghint">спроб: {s.view.guesses.length}</div>
          <GiveUp busy={g.busy} onGiveUp={() => void g.run(() => api.giveUp<View>("worldle", date))} />
        </div>
      )}

      <Toast message={g.toast} />
      {g.sheetOpen && finished && (
        <ResultSheet
          state={s}
          archive={Boolean(date)}
          title={s.status === "won" ? "Ціль захоплено" : "Здався"}
          subtitle={
            s.status === "won"
              ? `${s.view.guesses.length} спроб · стрік ${s.me.streak} 🔥`
              : `це був ${s.view.answer?.name ?? "?"} · стрік згорів`
          }
          scoreLabel="avg спроб"
          formatScore={(n) => `${n} спроб`}
          onDismiss={() => g.setSheetOpen(false)}
        >
          {s.view.answer && (
            <div className="mt-4 flex items-center justify-center gap-3">
              <img src={`/flags/${s.view.answer.code.toLowerCase()}.svg`} alt="" className="h-8 rounded-sm" />
              <span className="text-lg font-semibold">{s.view.answer.name}</span>
            </div>
          )}
        </ResultSheet>
      )}
    </Shell>
  );
}
